import React, {useEffect, useState} from 'react';
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Typography
} from '@material-ui/core';
import { AlertDialog, DraggableDialog, PromptDialog } from '../dialogs';
import { ipcRenderer } from '../electron-modules';
import clsx from 'clsx';
import {
  changeBasicAdjustments,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraTransform,
  selectActiveCamera
} from '../redux/camera-slice';
import { useSelector, useDispatch } from 'react-redux';
import { saveProject } from '../project-utils';
import { trackToolUsage } from "../executable";
import { Viewer } from '../viewer/js/main';
import { toast } from '../app';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    minHeightContent: {
      minHeight: "100px"
    },
    titleTop: {
      marginTop: theme.spacing(2.0)
    },
    warning: {
      color: theme.palette.warning.main
    },
    success: {
      color: theme.palette.success.main
    }
  }),
);

const AlignerShortcuts = () => {
  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Roll (-):</Box> Keypad 7
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Roll (+):</Box> Keypad 9
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Pitch (-):</Box> Keypad 4
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Pitch (+):</Box> Keypad 6
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Yaw (-):</Box> Keypad 1
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>Adjust Yaw (+):</Box> Keypad 3
          </Typography>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export const ImageAlignerBasic = (props) => {
  const viewer = props.viewer as Viewer;
  const {setDefaultTab, setTabsEnabled, singleAlignment,
    alignerBasicOpen, alignerAdvOpen, setAlignerBasicOpen} = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const [alertOpen, setAlertOpen] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [hasTransform, setHasTransform] = useState(false);
  const currentCamera = useSelector(selectActiveCamera);
  const basicAdjustments = useSelector(selectBasicAdjustments);
  const cameraTransformPath = useSelector(selectCameraTransform);

  const handleOpen = () => {
    if (alignerAdvOpen) {
      setAlertOpen(true);
      return;
    }

    setAlignerBasicOpen(true);
    setDefaultTab();
  };

  const handleClose = () => {
    setAlignerBasicOpen(false);
    setClosePrompt(false);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  const resetAligner = () => {
    viewer?.resetBasicAlignerCamera(savedAngle);

    toast.success(`Alignment Reset`);
  };

  const saveAdjustment = () => {
    const updated = {...basicAdjustments};
    updated[currentCamera] = alignment;
    dispatch(changeBasicAdjustments(updated));
    viewer?.resetBasicAlignerCamera(alignment);
    saveProject(false);

    trackToolUsage("aligner-basic");

    toast.success(`Alignment Saved`);
  };

  const cancelAdvancedAlignment = () => {
    dispatch(changeCameraTransform(null));
    saveProject(false);
  };

  const checkDataModified = () => {
    if (!hasActiveCamera) {
      return false;
    }

    const epsilon = 1e-10;
    const dx = (savedAngle[0] - singleAlignment[0]) < epsilon;
    const dy = (savedAngle[1] - singleAlignment[1]) < epsilon;
    const dz = (savedAngle[2] - singleAlignment[2]) < epsilon;

    return !(dx && dy && dz);
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-image-aligner-basic");
    ipcRenderer?.on("open-image-aligner-basic", handleOpen);
  }, [alignerAdvOpen]);

  useEffect(() => {
    setHasTransform(cameraTransformPath !== null);
  }, [viewer, cameraTransformPath]);

  useEffect(() => {
    setTabsEnabled(!alignerBasicOpen);
    const enabled = hasActiveCamera && alignerBasicOpen;
    viewer?.setBasicAlignerState(enabled);
  }, [viewer, alignerBasicOpen, currentCamera]);

  useEffect(() => {
    // Not applicable if transform matrix is applied
    if (hasTransform) {
      return;
    }

    viewer?.resetBasicAlignerCamera(savedAngle);
  }, [viewer, currentCamera]);

  const hasActiveCamera = currentCamera !== null;
  const activeCameraName = hasActiveCamera ?
    viewer?.getCurrentCameraName() : null;

  const alignment = hasActiveCamera
    ? singleAlignment.map(x => parseFloat(x.toFixed(2)))
    : [0,0,0];

  const savedAngle = currentCamera in basicAdjustments
    ? basicAdjustments[currentCamera]
    : [0, 0, 0];

  const isModified = checkDataModified();

  return (
    <React.Fragment>
      <DraggableDialog
        open={alignerBasicOpen}
        title="Data Aligner (Basic)"
        onClose={checkClose}
      >
        <DialogContent className={classes.minHeightContent}>

          {(hasTransform) && (<Typography variant="caption" className={classes.warning}>
            A global transformation is currently applied. Current alignment must be cleared
            before adding per-camera corrections
          </Typography>)}

          {(hasTransform === false) && (<React.Fragment>
            {/* Current camera section */}
            <React.Fragment>
              <Typography gutterBottom color="textSecondary">
                Current Camera:
              </Typography>

              <Typography variant="caption">
                {hasActiveCamera ? activeCameraName : "No Camera Selected"}
              </Typography>
            </React.Fragment>

            {/* Current adjustment section */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                Current Adjustment:
              </Typography>

              <Typography variant="caption">
                {hasActiveCamera  ? `[${alignment[0]}, ${alignment[1]}, ${alignment[2]}]` : "N/A"}
              </Typography>
            </React.Fragment>

            {/* Current adjustment section */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                Alignment Status:
              </Typography>

              <Typography variant="caption" className={clsx("", {
                [classes.success]: !isModified,
                [classes.warning]: isModified
              })}>
                {isModified ? "Modified" : "Saved"}
              </Typography>
            </React.Fragment>

            {/* Alignment shortcut keys */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                Alignment Shortcuts:
              </Typography>

              <AlignerShortcuts/>
            </React.Fragment>
          </React.Fragment>)}

        </DialogContent>

        <DialogActions>
          {(hasTransform) && (<Button onClick={cancelAdvancedAlignment} color="secondary">
            Clear Alignment Correction
          </Button>)}

          {(hasTransform === false) && (<React.Fragment>
            <Button
              color="secondary"
              disabled={!hasActiveCamera}
              onClick={resetAligner}
            >
              Reset Values
            </Button>

            <Button
              color="primary"
              disabled={!hasActiveCamera}
              onClick={saveAdjustment}
            >
              Save Adjustment
            </Button>
          </React.Fragment>)}
        </DialogActions>
      </DraggableDialog>

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          viewer?.resetBasicAlignerCamera(savedAngle);
          handleClose();
        }}
        title="Close Aligner?"
        prompt={`Do you want to close the data aligner? Current modifications will not be saved.`}
        button="Close"
      />

      <AlertDialog
        open={alertOpen}
        title="Data Aligner"
        handleClose={handleAlertClose}
      >
        <DialogContentText>
          Unable to open basic data aligner if advanced mode is open
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};